import CustomButton from 'Components/CustomButton'
import { ListSubheader, useTheme } from '@mui/material'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import Paper from '@mui/material/Paper'
import { styled } from '@mui/material/styles'
import * as React from 'react'
import { t } from 'ttag'

const StyledListSubheader = styled(ListSubheader)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white'
}))

// This function is used to get the difference between two arrays
function not<T>(a: T[], b: T[], compareFn: (item: T) => any) {
  return a.filter(value => !b.some(bItem => compareFn(value) === compareFn(bItem)))
}

// This function is used to get the intersection between two arrays
function intersection<T>(a: T[], b: T[], compareFn: (item: T) => any) {
  return a.filter(value => b.some(bItem => compareFn(value) === compareFn(bItem)))
}

type CustomTransferListProps<T> = {
  leftItemsHeaderText: string
  rightItemsHeaderText: string
  leftItems: T[]
  rightItems: T[]
  renderItemContent: (item: T) => React.ReactNode
  handleSave: (right: T[]) => void
  handleCancel: () => void
  allowTransferBack?: boolean
  compareFn: (item: T) => any // Function to compare items
  filterFn?: (items: T[]) => T[] // Function to filter items
}

function CustomTransferList<T>({
  leftItemsHeaderText,
  rightItemsHeaderText,
  leftItems,
  rightItems,
  renderItemContent,
  handleSave,
  handleCancel,
  allowTransferBack = true,
  compareFn,
  filterFn
}: Readonly<CustomTransferListProps<T>>) {
  const theme = useTheme()
  const [checked, setChecked] = React.useState<T[]>([])
  const [left, setLeft] = React.useState<T[]>(leftItems)
  const [right, setRight] = React.useState<T[]>(rightItems)

  const leftChecked = intersection(checked, left, compareFn)
  const rightChecked = intersection(checked, right, compareFn)

  const handleToggle = (value: T) => () => {
    const currentIndex = checked.findIndex(item => compareFn(item) === compareFn(value))
    const newChecked = [...checked]

    if (currentIndex === -1) {
      newChecked.push(value)
    } else {
      newChecked.splice(currentIndex, 1)
    }

    setChecked(newChecked)
  }

  const handleAllRight = () => {
    const leftItems = filterFn ? filterFn(left) : left
    setRight(leftItems.concat(right))
    setLeft(not(left, leftItems, compareFn))
  }

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked))
    setLeft(not(left, leftChecked, compareFn))
    setChecked(not(checked, leftChecked, compareFn))
  }

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked))
    setRight(not(right, rightChecked, compareFn))
    setChecked(not(checked, rightChecked, compareFn))
  }

  const handleAllLeft = () => {
    const rightItems = filterFn ? filterFn(right) : right
    setLeft(rightItems.concat(left))
    setRight(not(right, rightItems, compareFn))
  }

  const availableItems = filterFn ? filterFn(left) : left
  const selectedItems = filterFn ? filterFn(right) : right

  const customList = (items: T[], listSubHeaderText: string) => (
    <Paper
      sx={{
        width: '100%',
        height: 500,
        overflow: 'auto',
        ...(theme.palette.mode === 'light' && { background: 'white' })
      }}
    >
      <List
        dense
        component="div"
        subheader={<StyledListSubheader>{listSubHeaderText}</StyledListSubheader>}
      >
        {items.map(item => {
          return (
            <ListItemButton
              selected={checked.some(checkedItem => compareFn(checkedItem) === compareFn(item))}
              key={compareFn(item)}
              onClick={handleToggle(item)}
            >
              {renderItemContent(item)}
            </ListItemButton>
          )
        })}
      </List>
    </Paper>
  )

  return (
    <>
      <Grid container spacing={2} sx={{ p: theme.spacing(2), alignItems: 'center' }}>
        <Grid size={5}>{customList(availableItems, leftItemsHeaderText)}</Grid>
        <Grid size={2}>
          <Grid container direction="column" sx={{ alignItems: 'center' }}>
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              color="inherit"
              onClick={handleAllRight}
              disabled={left.length === 0}
              aria-label={t`Move all right`}
            >
              ≫
            </Button>
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              color="inherit"
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              aria-label={t`Move selected right`}
            >
              &gt;
            </Button>
            {allowTransferBack && (
              <>
                <Button
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  size="small"
                  color="inherit"
                  onClick={handleCheckedLeft}
                  disabled={rightChecked.length === 0}
                  aria-label={t`Move selected left`}
                >
                  &lt;
                </Button>
                <Button
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  size="small"
                  color="inherit"
                  onClick={handleAllLeft}
                  disabled={right.length === 0}
                  aria-label={t`Move all left`}
                >
                  ≪
                </Button>
              </>
            )}
          </Grid>
        </Grid>
        <Grid size={5}>{customList(selectedItems, rightItemsHeaderText)}</Grid>
      </Grid>
      <Grid container spacing={2} sx={{ p: theme.spacing(2), alignItems: 'center' }}>
        <Grid size={6}>
          <Button
            variant="outlined"
            color="inherit"
            sx={{ width: '100%' }}
            onClick={handleCancel}
          >{t`Cancel`}</Button>
        </Grid>
        <Grid size={6}>
          <CustomButton
            sx={{ width: '100%' }}
            onClick={() => handleSave(right)}
          >{t`Save changes`}</CustomButton>
        </Grid>
      </Grid>
    </>
  )
}

export default CustomTransferList
